import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Building2 } from "lucide-react";
import { Banner, Button, Chip, EmptyState, Page, PageHeader, Tabs, TabsPanel, type TabItem } from "@/components/ds";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OrgOverviewTab } from "@/components/corporate/organizations/page/OrgOverviewTab";
import { OrgPeopleTab } from "@/components/corporate/organizations/page/OrgPeopleTab";
import { OrgActivityTab } from "@/components/corporate/organizations/page/OrgActivityTab";
import { OrgIntegrationsTab } from "@/components/corporate/organizations/page/OrgIntegrationsTab";
import {
  OrgSettingsTab,
  SETTINGS_SECTIONS,
  isSettingsSection,
  sectionLabel,
} from "@/components/corporate/organizations/page/OrgSettingsTab";
import type { SettingsSectionKey } from "@/components/corporate/organizations/page/SectionFrame";
import { appDisplayName, portalAddress } from "@/components/corporate/organizations/orgOptions";
import { useOrgEntraSyncStatus } from "@/hooks/useOrgEntraSyncStatus";
import type { Organization } from "@/types/organizations";

type TabKey = "overview" | "people" | "activity" | "settings" | "integrations";

const TABS: TabItem[] = [
  { value: "overview", label: "Overview", testId: "org-tab-overview" },
  { value: "people", label: "People", testId: "org-tab-people" },
  { value: "activity", label: "Activity", testId: "org-tab-activity" },
  { value: "settings", label: "Settings", testId: "org-tab-settings" },
  { value: "integrations", label: "Integrations", testId: "org-tab-integrations" },
];

const DEFAULT_SECTION: SettingsSectionKey = SETTINGS_SECTIONS[0].key;

interface Nav {
  tab: TabKey;
  section: SettingsSectionKey;
}

/** `#people`, `#settings`, `#settings-branding`, … — so a link can open a tab or a section. */
export function parseOrgHash(hash: string, current: SettingsSectionKey = DEFAULT_SECTION): Nav {
  const value = hash.replace(/^#/, "");
  if (value === "settings") return { tab: "settings", section: current };
  if (value.startsWith("settings-")) {
    const section = value.slice("settings-".length);
    return { tab: "settings", section: isSettingsSection(section) ? section : DEFAULT_SECTION };
  }
  if (value === "people" || value === "activity" || value === "integrations" || value === "overview") {
    return { tab: value, section: current };
  }
  return { tab: "overview", section: current };
}

export const orgHash = (nav: Nav) => (nav.tab === "settings" ? `#settings-${nav.section}` : `#${nav.tab}`);

const sameNav = (a: Nav, b: Nav) => a.tab === b.tab && (a.tab !== "settings" || a.section === b.section);

const currentHash = () => (typeof window === "undefined" ? "" : window.location.hash);

function setHash(nav: Nav) {
  const next = orgHash(nav);
  if (window.location.hash !== next) window.history.replaceState(window.history.state, "", next);
}

/**
 * Organization detail (`/organizations/:organizationId`) in the new CMS look: five tabs — Overview,
 * People, Activity, Settings, Integrations — with the tab (and Settings section) in the URL hash.
 * Leaving a Settings section with unsaved edits asks "Discard your changes to <section>?".
 */
export default function ClientDetailPage() {
  const { organizationId } = useParams();
  const [, navigate] = useLocation();
  const [nav, setNav] = useState<Nav>(() => parseOrgHash(currentHash()));
  const [dirtySection, setDirtySection] = useState<SettingsSectionKey | null>(null);
  const [pending, setPending] = useState<Nav | null>(null);
  // The control that asked to leave (tab, section or link): focus goes back to it when the
  // discard dialog closes, never to <body>.
  const openerRef = useRef<HTMLElement | null>(null);
  // Focusing a tab activates it (Radix, automatic mode): the focus we give back must not navigate.
  const restoringFocusRef = useRef(false);
  const navRef = useRef(nav);
  navRef.current = nav;
  const dirtyRef = useRef(dirtySection);
  dirtyRef.current = dirtySection;

  const entra = useOrgEntraSyncStatus(organizationId);
  const query = useQuery<Organization | null>({
    queryKey: [`/organizations/${organizationId}`],
    enabled: !!organizationId,
  });
  const organization = query.data ?? null;

  const apply = useCallback((target: Nav) => {
    setNav(target);
    setHash(target);
  }, []);

  /** Every way of moving (tab, section, hash link) passes here, so unsaved edits are never lost silently. */
  const go = useCallback(
    (target: Nav, fromHashChange = false, opener?: HTMLElement | null) => {
      const current = navRef.current;
      if (sameNav(target, current)) {
        if (fromHashChange) setHash(current);
        return;
      }
      if (dirtyRef.current && current.tab === "settings") {
        if (fromHashChange) setHash(current);
        const active = document.activeElement;
        openerRef.current = opener ?? (active instanceof HTMLElement && active !== document.body ? active : null);
        setPending(target);
        return;
      }
      apply(target);
    },
    [apply],
  );

  useEffect(() => {
    const onHashChange = () => go(parseOrgHash(currentHash(), navRef.current.section), true);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [go]);

  // A reload or a closed tab with unsaved edits asks the browser's own question.
  useEffect(() => {
    if (!dirtySection) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirtySection]);

  const onDirtyChange = useCallback((section: SettingsSectionKey, dirty: boolean) => {
    setDirtySection((prev) => (dirty ? section : prev === section ? null : prev));
  }, []);

  // A failed background re-read keeps the page on its last good data.
  if (query.isLoading && !organization) {
    return (
      <Page data-testid="org-page-loading">
        <div className="mb-4 h-7 w-1/3 animate-pulse rounded-ds bg-ds-sunk" />
        <div className="mb-4 h-9 w-2/3 animate-pulse rounded-ds bg-ds-sunk" />
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-ds-lg bg-ds-sunk" />
          ))}
        </div>
      </Page>
    );
  }

  if (query.isError && !organization) {
    return (
      <Page data-testid="org-page-error">
        <Banner
          tone="err"
          icon={AlertCircle}
          action={
            <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        >
          <b>Couldn&apos;t load this company.</b> Check your connection and try again.
        </Banner>
      </Page>
    );
  }

  if (!organization) {
    return (
      <Page data-testid="org-page-empty">
        <EmptyState
          icon={Building2}
          title="Company not found"
          text="It may have been removed, or the link is wrong."
          action={{ label: "Back to organizations", onClick: () => navigate("/organizations") }}
        />
      </Page>
    );
  }

  const ownApp = organization.usesAthlynApp !== true;

  return (
    <Page data-testid="org-page">
      <Button
        variant="ghost"
        size="sm"
        className="mb-2 -ml-2.5"
        icon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
        onClick={() => navigate("/organizations")}
      >
        Organizations
      </Button>
      <PageHeader
        title={organization.name}
        subtitle={<span data-testid="org-portal-address">{portalAddress(organization.subdomain)}</span>}
        actions={
          <>
            <Chip tone={organization.isActive ? "ok" : "neutral"} dot>
              {organization.isActive ? "Active" : "Inactive"}
            </Chip>
            {ownApp ? (
              <Chip tone="warn">Own app · {appDisplayName(organization.appScheme)}</Chip>
            ) : (
              <Chip tone="info">Athlyn app</Chip>
            )}
            {entra.data?.isEnabled && <Chip tone="neutral">Microsoft sign-in</Chip>}
          </>
        }
      />

      <Tabs
        label="Company"
        items={TABS}
        wrap
        strongLabels
        value={nav.tab}
        onValueChange={(value) =>
          !restoringFocusRef.current &&
          go(
            { tab: value as TabKey, section: nav.section },
            false,
            document.querySelector<HTMLElement>(`[data-testid="org-tab-${value}"]`),
          )
        }
      >
        <TabsPanel value="overview">
          <OrgOverviewTab organization={organization} />
        </TabsPanel>
        <TabsPanel value="people">
          <OrgPeopleTab organizationId={organization.id} />
        </TabsPanel>
        <TabsPanel value="activity">
          <OrgActivityTab organizationId={organization.id} />
        </TabsPanel>
        <TabsPanel value="settings">
          <OrgSettingsTab
            organization={organization}
            section={nav.section}
            onSelectSection={(section, opener) => go({ tab: "settings", section }, false, opener)}
            dirtySection={dirtySection}
            onDirtyChange={onDirtyChange}
          />
        </TabsPanel>
        <TabsPanel value="integrations">
          <OrgIntegrationsTab organizationId={organization.id} />
        </TabsPanel>
      </Tabs>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent
          data-testid="discard-changes-confirm"
          onCloseAutoFocus={(event) => {
            const opener = openerRef.current;
            openerRef.current = null;
            if (opener && opener.isConnected) {
              event.preventDefault();
              restoringFocusRef.current = true;
              try {
                opener.focus();
              } finally {
                restoringFocusRef.current = false;
              }
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Discard your changes to {sectionLabel(dirtySection ?? nav.section)}?</AlertDialogTitle>
            <AlertDialogDescription>You changed something here but didn&apos;t save it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                const target = pending;
                setPending(null);
                if (target) {
                  setDirtySection(null);
                  apply(target);
                }
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
